import { useState, useEffect } from "react";

import {
  Button, Typography, ArrowButton,
  TextField, Modal, InfoPanel,
  Tooltip, IconWrapper, Select
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import {
  uploadDocument,
  DocumentUploadRequest,
  CreateInstitutionRequest,
  createInstitutionRequest,
  getAndCheckInstitutionRequest
} from "../../api/nisooApi";
import StaticField from "../../components/StaticField";
import UploadButton from "../../components/UploadButton";
import { useInput } from "../../hooks";
import { DocumentType } from "../../models/Document";
import {
  Founder,
  Institution, InstitutionLocation,
  institutionLocationInitial,
  InstitutionValidationScheme,
  LocationDeleteState, LocationState,
  institutionInitial,
  OIBServiceCheckStatus,
  AndragogicLeader,
  AndragogicLeaderInitial,
  AndragogicLeaderState,
  AndragogicLeaderDeleteState,
  InstitutionType
} from "../../models/Institution";
import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetInstitutionTypeQuery } from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { institutionEditRoute, institutionDetailsRoute, dashboardRoute, programRequestListRoute } from "../../routes/routes";
import AndragogicLeaderPanel from "./AndragogicLeaderPanel";
import AndragogicLeaderPanelV2 from "./AndragogicLeaderPanelV2";
import FounderPanel from "./FounderPanel";
import styles from "./Institution.module.scss";
import LocationPanel from "./LocationPanel";


export default function InstitutionEdit() {
  const { t } = useTranslation();
  const { data: institutionTypes } = useGetInstitutionTypeQuery();
  const history = useHistory();
  const location = useLocation();

  const [institution, setInstitution] = useState<Institution>(institutionInitial);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openConfirmCloseModal, setOpenConfirmCloseModal] = useState(false);
  const [sourceInstitution, setSourceInstitution] = useState<string>("");
  const [nextPageToOpen, setNextPage] = useState<string>("");
  const { data: userSession } = useGetUserSessionQuery();
  const editInstitutionType = false;

  useEffect(() => {
    const fetchInstitution = async () => {
      if (!userSession) {
        return;
      }

      var institutionId = (location.state as any)?.id || userSession.institutionId!;

      var results = await getAndCheckInstitutionRequest(institutionId);
      setInstitution(results);
      setSourceInstitution(JSON.stringify(results));
    }
    fetchInstitution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  const [errors, doValidations, stopValidations] = useValidation({
    data: institution,
    validationSchema: InstitutionValidationScheme,
  });

  const handleConfirmCancelSaveModal = async () => {
    setOpenConfirmCloseModal(true);
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
    history.push(institutionEditRoute());
  }

  const handleConfirmedSuccessModal = () => {
    setOpenSuccessModal(false)
    navigateToInstitutionDetail();
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);

    await uploadInstitutionDocuments();
    await uploadLocationsDocuments();
    await uploadAndragogicLeadersDocuments();

    var request: CreateInstitutionRequest = {
      id: institution.id,
      oib: institution.oib,
      name: institution.name,
      institutionTypeId: institution.institutionTypeId,
      townId: institution.town?.id,
      phoneNumber: institution.phoneNumber.trim(),
      email: institution.email.trim(),
      address: institution.address,
      documentCourtRegister: institution.documentCourtRegister?.id,
      documentFoundingAct: institution.documentFoundingAct?.id,
      documentUsageAgreement: institution.documentUsageAgreement?.id,
      documentMinimumRequirement: institution.documentMinimumRequirement?.id,
      founders: institution.founders.map(
        (fd: Founder) => {
          return {
            id: fd.id,
            name: fd.name,
            oib: fd.oib,
            mbg: fd.mbg,
            commercialCourt: fd.commercialCourt
          }
        }
      ),
      locations: institution.locations.map(
        (loc: InstitutionLocation) => {
          return {
            Id: loc.id,
            locationDetailId: loc.locationDetailId,
            locationTypeId: loc.locationTypeId,
            townId: loc.town.id,
            address: loc.address,
            locationUsageAgreement: loc.locationUsageAgreement?.id,
            locationMinimumRequirementSolution: loc.locationMinimumRequirementSolution?.id,
            isActive: loc.activityState === LocationState.Active,
            isDeleted: loc.deleteState === LocationDeleteState.Deleted,
            deactivateDate: loc.deactivateDate
          }
        }
      ),
      andragogicLeaders: institution.andragogicLeaders
        .map(
          (al: AndragogicLeader) => {
            return {
              id: al.id,
              oib: al.oib,
              firstName: al.firstName,
              lastName: al.lastName,
              dateFrom: al.dateFrom,
              dateTo: al.dateTo,
              copyOdDiploma: al.copyOdDiploma?.id,
              additionalDocument: al.additionalDocument?.id,
              locations: al.locations,
              isActive: al.activityState === AndragogicLeaderState.Active,
              isDeleted: al.deleteState === AndragogicLeaderDeleteState.Deleted,
              deactivateDate: al.deactivateDate
            }
          }
        )
    }

    await createInstitutionRequest(request).then(() => {
      setOpenSuccessModal(true);
    });
  }

  const uploadInstitutionDocuments = async () => {
    if (institution.documentFoundingAct && institution.documentFoundingAct.id === -1) {
      var documentFoundingActRequest: DocumentUploadRequest = {
        document: institution.documentFoundingAct!.file!,
        documentTypeId: DocumentType.DocumentFoundingAct        
      };
      await uploadDocument(documentFoundingActRequest).then((idDoc) => {
        var documentFoundingAct = institution.documentFoundingAct;
        documentFoundingAct!.id = idDoc;
        setInstitution((prev) => ({
          ...prev,
          documentFoundingAct: documentFoundingAct
        }));
      });

    }
    if (institution.documentUsageAgreement && institution.documentUsageAgreement.id === -1) {
      var documentUsageAgreementRequest: DocumentUploadRequest = {
        document: institution.documentUsageAgreement!.file!,
        documentTypeId: DocumentType.UsageAgreement
      };
      await uploadDocument(documentUsageAgreementRequest).then((idDoc) => {
        var documentUsageAgreement = institution.documentUsageAgreement;
        documentUsageAgreement!.id = idDoc;
        setInstitution((prev) => ({
          ...prev,
          documentUsageAgreement: documentUsageAgreement
        }));
      });
    }
    if (institution.documentMinimumRequirement && institution.documentMinimumRequirement.id === -1) {
      var documentMinimumRequirementRequest: DocumentUploadRequest = {
        document: institution.documentMinimumRequirement!.file!,
        documentTypeId: DocumentType.MinimumRequirementSolution
      };
      await uploadDocument(documentMinimumRequirementRequest).then((idDoc) => {
        var documentMinimumRequirement = institution.documentMinimumRequirement;
        documentMinimumRequirement!.id = idDoc;
        setInstitution((prev) => ({
          ...prev,
          documentMinimumRequirement: documentMinimumRequirement
        }));
      });
    }
  }

  const uploadLocationsDocuments = async () => {
    const promises = [] as Promise<void>[];

    if (institution.locations && institution.locations.length > 0) {
      institution.locations.map(async (location, locationIndex) => {
        if (location.locationUsageAgreement && location.locationUsageAgreement.id === -1) {
          var locationUsageAgreementRequest: DocumentUploadRequest = {
            document: location.locationUsageAgreement!.file!,
            documentTypeId: DocumentType.LocationUsageAgreement            
          };
          const locationUsageAgreementPromise = uploadDocument(locationUsageAgreementRequest).then((idDoc) => {
            var locationsList = institution.locations!.slice();
            if (locationsList && locationsList.length > 0) {
              locationsList[locationIndex].locationUsageAgreement!.id = idDoc;
              setInstitution((prev) => ({
                ...prev,
                locations: locationsList
              }));
            }
          });
          promises.push(locationUsageAgreementPromise);
        }
        if (location.locationMinimumRequirementSolution && location.locationMinimumRequirementSolution.id === -1) {
          var locationMinimumRequirementSolutionRequest: DocumentUploadRequest = {
            document: location.locationMinimumRequirementSolution!.file!,
            documentTypeId: DocumentType.LocationMinimumRequirementSolution            
          };
          const locationMinimumRequirementSolutionPromise = uploadDocument(locationMinimumRequirementSolutionRequest).then((idDoc) => {
            var locationsList = institution.locations!.slice();
            if (locationsList && locationsList.length > 0) {
              locationsList[locationIndex].locationMinimumRequirementSolution!.id = idDoc;
              setInstitution((prev) => ({
                ...prev,
                locations: locationsList
              }));
            }
          });

          promises.push(locationMinimumRequirementSolutionPromise);
        }
      });
      await Promise.all(promises);
    }
  }

  const uploadAndragogicLeadersDocuments = async () => {
    const promises = [] as Promise<void>[];

    if (institution.andragogicLeaders && institution.andragogicLeaders.length > 0) {
      institution.andragogicLeaders.map(async (andragogicLeader, andragogicLeaderIndex) => {
        if (andragogicLeader.copyOdDiploma && andragogicLeader.copyOdDiploma.id === -1) {
          var copyOfDiplomaRequest: DocumentUploadRequest = {
            document: andragogicLeader.copyOdDiploma!.file!,
            documentTypeId: DocumentType.CopyOfDiploma            
          };
          const copyOfDiplomaPromise = uploadDocument(copyOfDiplomaRequest).then((idDoc) => {
            var andragogicLeadersList = institution.andragogicLeaders.slice();
            if (andragogicLeadersList && andragogicLeadersList.length > 0) {
              andragogicLeadersList[andragogicLeaderIndex].copyOdDiploma!.id = idDoc;
              setInstitution((prev) => ({
                ...prev,
                andragogicLeaders: andragogicLeadersList
              }));
            }
          });
          promises.push(copyOfDiplomaPromise);
        }
        if (andragogicLeader.additionalDocument && andragogicLeader.additionalDocument.id === -1) {
          var additionalDocumentRequest: DocumentUploadRequest = {
            document: andragogicLeader.additionalDocument!.file!,
            documentTypeId: DocumentType.AdditionalDocument            
          };
          const additionalDocumentPromise = uploadDocument(additionalDocumentRequest).then((idDoc) => {
            var andragogicLeadersList = institution.andragogicLeaders.slice();
            if (andragogicLeadersList && andragogicLeadersList.length > 0) {
              andragogicLeadersList[andragogicLeaderIndex].additionalDocument!.id = idDoc;
              setInstitution((prev) => ({
                ...prev,
                andragogicLeaders: andragogicLeadersList
              }));
            }
          });
          promises.push(additionalDocumentPromise);
        }
      });
      await Promise.all(promises);
    }
  }

  const navigateToDashboard = () => {
    if (userSession) {
      if (userSession.roleId === RoleEnum.InstitutionAdmin || userSession.roleId === RoleEnum.InstitutionDirector) {
        history.push(dashboardRoute());
      }
      else {
        history.push(programRequestListRoute());
      }
    }
  }

  const navigateToInstitutionDetail = () => {
    if (institution) {
      history.push(institutionDetailsRoute(), { id: institution.id });
    }
  }

  const handleCancelClose = () => {
    setOpenConfirmCloseModal(false);
  }

  const submitInstitution = () => {
    const isValid = doValidations();
    if (!isValid) {
      return;
    }
    setOpenConfirmModal(true);
  };

  const confirmExitPage = (nextPage: string) => {
    var currentInstitution = (JSON.stringify(institution));
    setNextPage(nextPage);

    if (currentInstitution !== sourceInstitution) {
      handleConfirmCancelSaveModal();
    }
    else {
      if (nextPage === "detail") {
        navigateToInstitutionDetail();
      }
      else if (nextPage === "dashboard") {
        navigateToDashboard();
      }
    }
  }

  const exitPage = () => {
    if (nextPageToOpen === "detail") {
      navigateToInstitutionDetail();
    }
    else {
      navigateToDashboard();
    }
  }

  const onCancelChanges = () => {
    confirmExitPage("detail");
  };

  const onAcceptOibData = () => {
    if (institution.addressFromOib) {
      institution.address = institution.addressFromOib;
      institution.addressFromOib = undefined;
    }
    if (institution.nameFromOib) {
      institution.name = institution.nameFromOib;
      institution.nameFromOib = undefined;
    }
    if (institution.townFromOib && institution.townFromOib.id > 0) {
      institution.town = institution.townFromOib;
      institution.townFromOib = undefined;
    }
    setInstitution(institution);
    history.push(institutionEditRoute());
  }

  const onBackToRequestList = () => {
    confirmExitPage("dashboard");
  };

  const handleAddLocation = () => {
    stopValidations();
    var newLocation: InstitutionLocation = institutionLocationInitial();
    institution.locations.push(newLocation);

    setInstitution((prev: Institution) => ({
      ...prev,
      locations: [...institution.locations]
    }));
  };

  const handleAddAndragogicLeader = () => {
    stopValidations();
    var newAndragogicLeader: AndragogicLeader = AndragogicLeaderInitial();
    institution.andragogicLeaders.push(newAndragogicLeader);

    setInstitution((prev: Institution) => ({
      ...prev,
      andragogicLeaders: [...institution.andragogicLeaders]
    }));
  };

  const { handleChange } = useInput();

  const handleDocumentFoundingActAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    const newDocumentFoundingAct: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };

    setInstitution((prev: Institution) => ({
      ...prev,
      documentFoundingAct: newDocumentFoundingAct
    }));
  }

  const handleDocumentFoundingActRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!institution.documentFoundingAct || institution.documentFoundingAct.id === undefined) {
      return;
    }

    setInstitution((prev: Institution) => ({
      ...prev,
      documentFoundingAct: undefined
    }));
  };

  const handleDocumentUsageAgreementAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    const newDocumentUsageAgreement: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };

    setInstitution((prev: Institution) => ({
      ...prev,
      documentUsageAgreement: newDocumentUsageAgreement
    }));
  }

  const handleDocumentUsageAgreementRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!institution.documentUsageAgreement || institution.documentUsageAgreement.id === undefined) {
      return;
    }

    setInstitution((prev: Institution) => ({
      ...prev,
      documentUsageAgreement: undefined
    }));
  };

  const handleMinimumRequirementSolutionAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    const newMinimumRequirementSolution: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };

    setInstitution((prev: Institution) => ({
      ...prev,
      documentMinimumRequirement: newMinimumRequirementSolution
    }));
  }

  const handleMinimumRequirementSolutionRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!institution.documentMinimumRequirement || institution.documentMinimumRequirement.id === undefined) {
      return;
    }

    setInstitution((prev: Institution) => ({
      ...prev,
      documentMinimumRequirement: undefined
    }));
  };

  const canEditOrCreateAndragogicLeader = () => {
    return userSession?.roleId === RoleEnum.InstitutionDirector || userSession?.roleId === RoleEnum.MZOM || userSession?.roleId === RoleEnum.Helpdesk || userSession?.roleId === RoleEnum.InstitutionAdmin
  };

  const getErrorForPath = (path: string): string | undefined => {
    path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    path = path.replace(/^\./, '');           // strip a leading dot
    var pathKeys = path.split('.');
    var result = errors as any;

    for (let i = 0; i < pathKeys.length; ++i) {
      var key = pathKeys[i];
      if (result && typeof (result) === 'object' && result.hasOwnProperty(key)) {
        result = result[key] as any;
      }
      else {
        return undefined;
      }
    }

    return result as string;
  }

  const getFoundingActLabel = (): string => {
    if (institution.institutionTypeId === InstitutionType.HighSchool) {
      return t("institution.decisionOfEstablishmentSchool");
    }
    if (institution.institutionTypeId === InstitutionType.College) {
      return t("institution.decisionOfEstablishmentCollege");
    }
    return t("institution.foundingAct");
  };

  const getUsageAgreementLabel = (): string => {
    if (institution.institutionTypeId === InstitutionType.HighSchool) {
      return t("institution.extractFromLandRegistry");
    }
    if (institution.institutionTypeId === InstitutionType.College) {
      return t("institution.extractFromLandRegistry");
    }
    return t("institution.usageAgreement");
  };

  const getMinimumRequirementsLabel = (): string => {
    if (institution.institutionTypeId === InstitutionType.HighSchool) {
      return t("institution.decisionOfBeginningSchool");
    }
    if (institution.institutionTypeId === InstitutionType.College) {
      return t("institution.decisionOfBeginningCollege");
    }
    return t("institution.minimumRequirements");
  };

  let cancelButton;

  if (institution.id !== 0) {

    cancelButton =
      <Button outline onClick={onCancelChanges} className={styles.actionButton}>
        {t("common.cancel")}
      </Button>
  }

  var updateFromOib = institution.addressFromOib || institution.nameFromOib || institution.townFromOib;
  var updateFromOibAlertRow;

  if (updateFromOib) {
    updateFromOibAlertRow =
      <Row className={styles.oibDataAlertRow}>
        <Col md={8}>
          <InfoPanel
            color="warning"
            message={t("institution.changesFromOibData")}
          />
        </Col>
        <Col md={4}>
          <Button outline onClick={onAcceptOibData} className={styles.actionButton}>
            {t("institution.acceptChangesFromOibData")}
          </Button>
        </Col>
      </Row>
  }

  return (
    <>

      {/* {institution && institution.oibServiceStatus === OIBServiceCheckStatus.IncompleteData &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("requests.mainInfoMessage")}
              color="success"
              fullWidth
            />
          </Col>
        </Row>} */}

      <Row className={styles.commandRow}>
        <Col md={6}>
          {userSession && userSession.roleId !== RoleEnum.InstitutionAdmin && userSession.roleId !== RoleEnum.InstitutionDirector &&
            <>
              {institution && institution.oibServiceStatus !== OIBServiceCheckStatus.IncompleteData &&
                <ArrowButton
                  backwards
                  ariaLabel=""
                  onClick={onBackToRequestList}
                />
              }
              {institution && institution.oibServiceStatus !== OIBServiceCheckStatus.IncompleteData &&
                <span className={styles.backButtonCaption}>
                  {t("common.returnToRequestList")}
                </span>
              }
            </>
          }
        </Col>
        <Col md={6}>
          <Button onClick={submitInstitution} className={styles.actionButton}>
            {t("common.saveChanges")}
          </Button>
          {cancelButton}
        </Col>
      </Row>
      {updateFromOibAlertRow}
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("common.lastModifiedDate")}
            value={institution && institution.oibServiceStatus !== OIBServiceCheckStatus.IncompleteData ? hrDate(institution.lastModified) : ''}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("common.user")}
            value={institution.user}
          />
        </Col>
      </Row>
      <Row className={styles.titleRow}>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.basicInformation")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("institution.institutionName")}
            value={institution.name}
            newValue={institution.nameFromOib}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.institutionOib")}
            value={institution.oib}
          />
        </Col>
        {!editInstitutionType &&
          <Col md={4}>
            <StaticField
              label={t("institution.institutionType")}
              value={institution.institutionType}
            />
          </Col>
        }
        {editInstitutionType &&
          <Col md={4}>
            <Select
              label={t("institution.institutionType")}
              name={nameof<typeof institution>("institutionTypeId")}
              value={institution.institutionTypeId}
              onChange={handleChange(setInstitution)}
              selectData={codeBookToSelectData(institutionTypes)}
              errorText={errors?.institutionTypeId?.toString()}
              className={styles.textFieldLabel}
            />
          </Col>
        }
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("institution.headquarterAddress")}
            value={institution.address}
            newValue={institution.addressFromOib}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.headquarterTown")}
            value={institution.town?.name ?? ""}
            newValue={institution.townFromOib ? institution.townFromOib.name : ""}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.headquarterMunicipality")}
            value={institution.town?.municipalityName ?? ""}
            newValue={institution.townFromOib ? institution.townFromOib.municipalityName : ""}
          />
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("institution.headquarterCounty")}
            value={institution.town?.countyName ?? ""}
            newValue={institution.townFromOib ? institution.townFromOib.countyName : ""}
          />
        </Col>
        <Col md={4}>
          <TextField
            label={t("institution.email")}
            value={institution.email}
            maxLength={50}
            name={nameof<typeof institution>("email")}
            onChange={handleChange(setInstitution)}
            errorText={errors?.email?.toString()}
            className={styles.textFieldLabel}
          />
        </Col>
        <Col md={4}>
          <TextField
            label={t("institution.phone")}
            value={institution.phoneNumber}
            maxLength={32}
            name={nameof<typeof institution>("phoneNumber")}
            onChange={handleChange(setInstitution)}
            errorText={errors?.phoneNumber}
            className={styles.textFieldLabel}
          />
        </Col>
      </Row>
      <Row className={styles.titleRow}>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.foundersInformation")}
          </Typography>
        </Col>
      </Row>

      {institution.founders.map((founder: Founder, founderIndex: number) => (
        <FounderPanel key={founder.id.toString()} founderData={founder}
          panelIndex={founderIndex} errors={errors} readOnly={false} />
      ))}

      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.institutionDocuments")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.documentRow}>
        <Col md={4} className={styles.uplaodButtonLabel}>
          <UploadButton
            label={getFoundingActLabel()}
            name={nameof<typeof institution>("documentFoundingAct")}
            onFileAdded={handleDocumentFoundingActAdded}
            onFileRemoved={handleDocumentFoundingActRemoved}
            value={institution.documentFoundingAct}
            toolTip={t("document.imagesAndPdfTooltip")}
            fileFilter={t("document.imagesAndPdfFilter")}
            errorText={errors?.documentFoundingAct?.toString()}
            documentType={DocumentType.DocumentFoundingAct}
          />
        </Col>
        <Col md={4} className={styles.uplaodButtonLabel}>
          <UploadButton
            label={getUsageAgreementLabel()}
            name={nameof<typeof institution>("documentUsageAgreement")}
            onFileAdded={handleDocumentUsageAgreementAdded}
            onFileRemoved={handleDocumentUsageAgreementRemoved}
            value={institution.documentUsageAgreement}
            toolTip={t("document.imagesAndPdfTooltip")}
            fileFilter={t("document.imagesAndPdfFilter")}
            errorText={errors?.documentUsageAgreement?.toString()}
            documentType={DocumentType.UsageAgreement}
          />
        </Col>
        <Col md={4} className={styles.uplaodButtonLabel}>
          <UploadButton
            label={getMinimumRequirementsLabel()}
            name={nameof<typeof institution>("documentMinimumRequirement")}
            onFileAdded={handleMinimumRequirementSolutionAdded}
            onFileRemoved={handleMinimumRequirementSolutionRemoved}
            value={institution.documentMinimumRequirement}
            toolTip={t("document.imagesAndPdfTooltip")}
            fileFilter={t("document.imagesAndPdfFilter")}
            errorText={errors?.documentMinimumRequirement?.toString()}
            documentType={DocumentType.MinimumRequirementSolution}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.locationsOutsideHeadquarter")}
            <div className={styles.infoTooltipPanel}>
              <Tooltip
                overlay={<div className={styles.infoTooltip}>{t("institution.locationUserInfo")}</div>}
                placement="right"
              >
                <IconWrapper color="primary">
                  <InfoIcon />
                </IconWrapper>
              </Tooltip>
            </div>
          </Typography>
        </Col>
      </Row>

      {institution.locations.map((location: InstitutionLocation, locationIndex: number) => {
        return location.deleteState !== LocationDeleteState.Deleted ?
          <LocationPanel
            name={`${nameof<typeof institution>("locations")}`}
            key={locationIndex.toString()}
            location={location}
            locations={institution.locations}
            panelIndex={locationIndex}
            readOnly={false}            
            getErrorForPath={getErrorForPath}
            onChange={handleChange(setInstitution)}
            institutionTypeId={institution.institutionTypeId}
            roleId={userSession?.roleId}
          />
          : undefined;
      })}

      <Row>
        <Col md={12} className={styles.addLocationButtonCol}>
          <Button outline icon={<AddIcon />} onClick={handleAddLocation} className={styles.actionButton}
          >
            {t("institution.addLocation")}
          </Button>
        </Col>
      </Row>

      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.andragogicLeader.title")}
          </Typography>
        </Col>
      </Row>

      {/* {institution.andragogicLeaders.map((andragogicLeader: AndragogicLeader, andragogicLeaderIndex: number) => {
        return andragogicLeader.deleteState !== AndragogicLeaderDeleteState.Deleted ?
        <AndragogicLeaderPanel 
          key={andragogicLeaderIndex.toString()} 
          andragogicLeaderData={andragogicLeader} 
          locations={institution.locations} 
          panelIndex={andragogicLeaderIndex} 
          readOnly={!canEditOrCreateAndragogicLeader()} 
          onChange={handleChange(setInstitution)}
          errors={errors}
        />
        : undefined 
      })} */}

      <AndragogicLeaderPanelV2
        name={`${nameof<typeof institution>("andragogicLeaders")}`}
        andragogicLeaders={institution.andragogicLeaders}
        locations={institution.locations}
        institution={institution}
        readOnly={!canEditOrCreateAndragogicLeader()}
        onChange={handleChange(setInstitution)}
        getErrorForPath={getErrorForPath}
        doValidations={doValidations}
      />

      {canEditOrCreateAndragogicLeader() &&
        <Row>
          <Col md={12} className={styles.addLocationButtonCol}>
            <Button outline icon={<AddIcon />} onClick={handleAddAndragogicLeader} className={styles.actionButton}
            >
              {t("institution.addAndragogicLeader")}
            </Button>
          </Col>
        </Row>
      }


      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.yesSaveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
      <Modal
        type="confirm"
        open={openConfirmCloseModal}
        cancelText={t("common.noProperCase")}
        confirmText={t("common.yesCancel")}
        onClose={handleCancelClose}
        onConfirm={exitPage}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureCancelChanges")}
          </Typography>
        </div>
        <div>
          <Typography component="h4" variant="medium">
            {t("common.areYouSureCancelChangesNote")}
          </Typography>
        </div>
      </Modal>
      <Modal
        type="message"
        open={openSuccessModal}
        confirmText={t("common.close")}
        onClose={handleConfirmedSuccessModal}
        onConfirm={handleConfirmedSuccessModal}
      >
        <div className={styles.modalMessageContentAlignCenter}>

          <Typography component="h1" variant="largeBold">
            {t("common.saveSucceed")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}
